// src/components/pipeline/PipelineListView.jsx
// List view: one sortable table of every visible lead. Default order puts
// attention leads (active, unparked, nothing scheduled) first, then next
// touch ascending; clicking Client / Stage / Next Touch sorts by that column
// and clicking again reverses. The table scrolls sideways on phones.
// Spec: docs/superpowers/specs/2026-07-28-pipeline-board-list-redesign.md
import React, { useState } from "react";
import { CalendarDays, Clock, FileText } from "lucide-react";
import LeadCardMenu from "./LeadCardMenu";
import { sortLeadsForList, needsAttentionLead } from "../../utils/pipelineView";
import { formatDate } from "../../utils/helper";
import { clientNames } from "../../utils/leadClients";
import { useStateValue } from "../../context/StateProvider";
import { canUseTexting, useUnreadTexts } from "../../utils/texting";
import { getRatingColor } from "../../utils/buyerActivity";

// Unread-texts chip — self-contained so every row gets it for free.
// All instances share ONE Firestore listener (see useUnreadTexts).
const UnreadTextsChip = ({ leadId }) => {
  const [{ user }] = useStateValue();
  const unread = useUnreadTexts(canUseTexting(user));
  const n = canUseTexting(user) ? unread.leads[leadId] || 0 : 0;
  if (!n) return null;
  return (
    <span className="text-[10px] font-bold text-white bg-themeColor rounded-full px-1.5 py-0.5 whitespace-nowrap">
      💬 {n}
    </span>
  );
};

// Colours come from the shared map, not a copy — see PipelineBoardView.
const RatingPill = ({ rating }) => {
  const r = String(rating || "").toUpperCase();
  const style = getRatingColor(r);
  if (!style) return null;
  return (
    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${style}`}>
      {r}
    </span>
  );
};

const NameAndBadges = ({ lead, wonLabel }) => (
  <span className="flex items-center gap-1.5 min-w-0">
    <span className="font-semibold text-[13.5px] text-slate-800 truncate">
      {clientNames(lead) || lead.contactName || "(unnamed lead)"}
    </span>
    <RatingPill rating={lead.rating} />
    <UnreadTextsChip leadId={lead.id} />
    {lead.status === "converted" && (
      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 whitespace-nowrap">
        ✓ {wonLabel}
      </span>
    )}
    {lead.status === "lost" && (
      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
        Lost
      </span>
    )}
    {lead.fileRequested && (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap">
        <FileText size={10} /> File
      </span>
    )}
  </span>
);

export default function PipelineListView({
  leads,
  stages,
  parkingStages = [],
  stageDot, // (stage) => JSX dot in the stage's color
  graduationStage,
  wonLabel = "Won",
  apptLabel = "Appt",
  renderSecondary, // (lead) => string/JSX — the per-pipeline secondary column
  canManage,
  onOpen,
  onDismiss, // (lead) => void — snoozes the lead's follow-up a week out
  onMarkLost, // (lead) => void — optional; ⋮ menu "Mark as Lost" on manageable rows
  onDelete, // (lead) => void — optional; ⋮ menu "Delete Lead" on manageable rows
}) {
  const [sortKey, setSortKey] = useState("attention");
  const [sortDir, setSortDir] = useState("asc");

  const clickHeader = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const rows = sortLeadsForList(leads, sortKey, sortDir, {
    stages,
    parkingStages,
  });

  const SortableTh = ({ label, sort }) => (
    <th className="px-3 py-2 text-left">
      <button
        type="button"
        onClick={() => clickHeader(sort)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${
          sortKey === sort ? "text-linkColor" : "text-slate-500 hover:text-slate-700"
        }`}
      >
        {label}
        {sortKey === sort && (
          <span aria-hidden="true">{sortDir === "asc" ? "▲" : "▼"}</span>
        )}
      </button>
    </th>
  );

  const NextTouchCell = ({ lead }) => {
    if (String(lead.appointmentAt || "").trim())
      return (
        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium whitespace-nowrap">
          <CalendarDays size={13} /> {apptLabel} {formatDate(lead.appointmentAt)}
        </span>
      );
    if (String(lead.nextFollowUp || "").trim())
      return (
        <span className="inline-flex items-center gap-1 text-slate-600 whitespace-nowrap">
          <Clock size={13} /> Follow-up {formatDate(lead.nextFollowUp)}
        </span>
      );
    if (needsAttentionLead(lead, { parkingStages }))
      return (
        <span className="inline-flex items-center gap-1 text-amber-600 font-medium whitespace-nowrap">
          none
          <span aria-hidden="true">·</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(lead);
            }}
            className="underline underline-offset-2 hover:text-amber-800"
          >
            set one
          </button>
        </span>
      );
    return <span className="text-slate-400">-</span>;
  };

  const ActionButton = ({ children, onClick, title }) => (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="text-[12px] font-semibold border border-slate-200 hover:border-themeColor hover:text-themeColor rounded-lg px-2 py-1 text-slate-500 whitespace-nowrap"
    >
      {children}
    </button>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full min-w-[640px] text-[12.5px]">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/60">
            <SortableTh label="Client" sort="client" />
            <SortableTh label="Stage" sort="stage" />
            <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Details
            </th>
            <SortableTh label="Next Touch" sort="nextTouch" />
            <th className="px-3 py-2" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((lead) => {
            const stage = lead.stage || stages[0];
            const attention = needsAttentionLead(lead, { parkingStages });
            return (
              <tr
                key={lead.id}
                onClick={() => onOpen?.(lead)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onOpen?.(lead);
                }}
                tabIndex={0}
                className={`border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50 transition ${
                  lead.status !== "active" ? "opacity-60" : ""
                }`}
              >
                <td className="px-3 py-2.5">
                  <NameAndBadges lead={lead} wonLabel={wonLabel} />
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                    {stageDot?.(stage)} {stage}
                    {stage === graduationStage && (
                      <span className="text-[9px] font-bold text-linkColor bg-slate-50 rounded px-1 py-0.5">
                        → Deals Board
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-slate-500 max-w-[240px] truncate">
                  {renderSecondary?.(lead) || ""}
                </td>
                <td className="px-3 py-2.5">
                  <NextTouchCell lead={lead} />
                </td>
                <td className="px-3 py-2.5">
                  <span className="flex items-center justify-end gap-1.5">
                    <ActionButton title="Set a follow-up" onClick={() => onOpen?.(lead)}>
                      Follow-up
                    </ActionButton>
                    {attention && onDismiss && canManage?.(lead) && (
                      <ActionButton
                        title="Snoozes this lead one week out"
                        onClick={() => onDismiss(lead)}
                      >
                        Snooze
                      </ActionButton>
                    )}
                    <ActionButton title="Open lead" onClick={() => onOpen?.(lead)}>
                      Open
                    </ActionButton>
                    {canManage?.(lead) && (
                      <LeadCardMenu
                        lead={lead}
                        onMarkLost={onMarkLost}
                        onDelete={onDelete}
                      />
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
          {!rows.length && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                No leads match this view.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
